import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from '../../lib/AuthContext';
import api from '../../lib/api';
import TestimonialsAdmin from './testimonials.jsx';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    projects: 0,
    skills: 0,
    testimonials: 0,
    socialPosts: 0
  });
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  useEffect(() => {
    // Fetch dashboard data
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch projects count
        const projectsData = await api.projects.getAll();
        
        // For now, we'll use mock data for other stats
        // In a real implementation, you would fetch this from your API
        setStats({
          projects: projectsData.count || 0,
          skills: 5,
          testimonials: 2,
          socialPosts: 2
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(stats).map(([key, value]) => (
              <div key={key} className="bg-surface p-6 rounded-xl shadow-elevation-1 border border-border">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-body text-text-secondary capitalize">{key}</h3>
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon 
                      name={
                        key === 'projects' ? 'Briefcase' : 
                        key === 'skills' ? 'Code' : 
                        key === 'testimonials' ? 'MessageSquare' : 'Share2'
                      } 
                      size={20} 
                      className="text-primary"
                    />
                  </div>
                </div>
                <p className="font-heading text-3xl font-bold text-primary">{value}</p>
                <div className="mt-4 flex items-center text-xs">
                  <button 
                    onClick={() => setActiveTab(key === 'socialPosts' ? 'social' : key.toLowerCase())}
                    className="text-accent flex items-center"
                  >
                    <span>Manage {key}</span>
                    <Icon name="ArrowRight" size={14} className="ml-1" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        );
      case 'projects':
        return (
          <div className="bg-surface p-6 rounded-xl shadow-elevation-1 border border-border">
            <h3 className="font-heading text-xl font-bold text-primary mb-4">Projects Management</h3>
            <p className="font-body text-text-secondary">
              This section will allow you to add, edit, and delete projects.
            </p>
          </div>
        );
      case 'personal':
        return (
          <div className="bg-surface p-6 rounded-xl shadow-elevation-1 border border-border">
            <h3 className="font-heading text-xl font-bold text-primary mb-4">Personal Information</h3>
            <p className="font-body text-text-secondary">
              This section will allow you to update your personal information, including name, tagline, introduction, etc.
            </p>
          </div>
        );
      case 'skills':
        return (
          <div className="bg-surface p-6 rounded-xl shadow-elevation-1 border border-border">
            <h3 className="font-heading text-xl font-bold text-primary mb-4">Skills Management</h3>
            <p className="font-body text-text-secondary">
              This section will allow you to add, edit, and delete skills.
            </p>
          </div>
        );
      case 'testimonials':
        return <React.Suspense fallback={
          <div className="flex justify-center items-center py-20">
            <Icon name="Loader" size={40} className="animate-spin text-primary" />
          </div>
        }>
          <TestimonialsAdmin />
        </React.Suspense>;
      case 'social':
        return (
          <div className="bg-surface p-6 rounded-xl shadow-elevation-1 border border-border">
            <h3 className="font-heading text-xl font-bold text-primary mb-4">Social Posts Management</h3>
            <p className="font-body text-text-secondary">
              This section will allow you to add, edit, and delete social media posts.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center">
          <Icon name="Loader" size={40} className="text-primary animate-spin mb-4" />
          <p className="font-body text-text-secondary">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-accent to-warning rounded-lg flex items-center justify-center">
              <span className="text-surface font-heading font-bold text-lg">P</span>
            </div>
            <h3 className="font-heading font-bold text-xl">Portfolio Admin</h3>
          </div>
          
          <div className="flex items-center space-x-4">
            {user && (
              <span className="font-body text-text-secondary text-sm">
                Welcome, <span className="font-semibold">{user.username}</span>
              </span>
            )}
            
            <button 
              onClick={() => window.open('/', '_blank')}
              className="flex items-center text-text-secondary hover:text-primary transition-colors duration-200"
            >
              <Icon name="ExternalLink" size={18} className="mr-1" />
              <span className="font-body text-sm">View Site</span>
            </button>
            
            <button 
              onClick={handleLogout}
              className="flex items-center text-text-secondary hover:text-error transition-colors duration-200"
            >
              <Icon name="LogOut" size={18} className="mr-1" />
              <span className="font-body text-sm">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-surface rounded-xl shadow-elevation-1 border border-border p-4">
              <nav className="space-y-2">
                {[
                  { id: 'overview', label: 'Overview', icon: 'Grid' },
                  { id: 'projects', label: 'Projects', icon: 'Briefcase' },
                  { id: 'personal', label: 'Personal Info', icon: 'User' },
                  { id: 'skills', label: 'Skills', icon: 'Code' },
                  { id: 'testimonials', label: 'Testimonials', icon: 'MessageSquare' },
                  { id: 'social', label: 'Social Posts', icon: 'Share2' }
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                      activeTab === item.id
                        ? 'bg-primary text-surface'
                        : 'hover:bg-primary/10 text-text-secondary'
                    }`}
                  >
                    <Icon name={item.icon} size={18} />
                    <span className="font-body">{item.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="lg:col-span-4">
            <div className="mb-6">
              <h1 className="font-heading text-2xl font-bold text-primary">
                {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
              </h1>
              <p className="font-body text-text-secondary">
                Manage your portfolio content and settings
              </p>
            </div>
            
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 